import React, {useState, useEffect, useCallback} from 'react';
import {
  CollapsibleVideo,
  RemoteVideoContent,
  LocalVideoContent,
} from './WebRTCVideos';
import {
  CallControls,
  CallButton,
  ConnectionErrorDisplay,
} from './WebRTCControls';
import {Snackbar, Alert} from '@mui/material';
import {useRTCClient} from '../hooks/useRTCClient';
import {useSignaling} from '../hooks/useSignaling';
import {useMediaTracks} from '../hooks/useMediaTracks';
import {useScrollHandler} from '../hooks/useScrollHandler';

interface ViewingSessionRequest {
  rtcToken: string;
  signalingToken: string;
  appId: string;
  viewingSessionUserId: number;
  expireTime: number;
}

interface TourWebRTCProps {
  tourId: string;
  viewingSessionRequest: ViewingSessionRequest;
  isHost?: boolean;
  drawerIsOpen: boolean;
  drawerRef: React.RefObject<HTMLDivElement>;
}

export const TourWebRTC: React.FC<TourWebRTCProps> = React.memo(
  function TourWebRTC({
    drawerRef,
    drawerIsOpen,
    tourId,
    viewingSessionRequest,
    isHost = false,
  }) {
    const [tourDrawerWidth, setTourDrawerWidth] = useState(0);
    const [remoteVideoCollapsed, setRemoteVideoCollapsed] = useState(false);
    const [localVideoCollapsed, setLocalVideoCollapsed] = useState(false);
    const [showPermissionAlert, setShowPermissionAlert] = useState(false);

    // RTC client state
    const {
      rtcClient,
      isInCall,
      setIsInCall,
      connectionState,
      remoteUsers,
      connectionError,
      reconnectAttempts,
      // webRTCSupported,
      resetConnection,
    } = useRTCClient(tourId, viewingSessionRequest, isHost);

    // Signaling for sync
    const {
      isSyncActive,
      signalingConnection,
      signalingError,
      toggleSync,
      resetSignaling,
    } = useSignaling(tourId);

    // Local media tracks state
    const {
      localTracks,
      isMicActive,
      isVideoActive,
      mediaPermissions,
      isStartingCall,
      startCall,
      leaveCall,
      toggleMic,
      toggleVideo,
    } = useMediaTracks({
      rtcClient,
      isInCall,
      setIsInCall,
      tourId,
      viewingSessionRequest,
      isHost,
      toggleSync,
      connectionState,
    });

    // Scroll handler for syncing
    useScrollHandler(signalingConnection, tourId);

    // Reset connection and signaling
    const handleResetConnection = useCallback(() => {
      resetConnection();
      resetSignaling();
      leaveCall();
    }, [leaveCall, resetConnection, resetSignaling]);

    // Handle media permission changes
    useEffect(() => {
      if (!mediaPermissions.audio || !mediaPermissions.video) {
        setShowPermissionAlert(true);
      }
    }, [mediaPermissions]);

    // Update drawer width when it changes
    useEffect(() => {
      const updateDrawerWidth = () => {
        if (drawerRef.current) {
          setTourDrawerWidth(drawerRef.current.clientWidth);
        }
      };

      updateDrawerWidth();

      // Also update on window resize to handle responsive layouts
      window.addEventListener('resize', updateDrawerWidth);

      return () => {
        window.removeEventListener('resize', updateDrawerWidth);
      };
    }, [drawerRef, drawerIsOpen]);

    // Clean up on unmount
    useEffect(() => {
      return () => {
        leaveCall();
      };
    }, [leaveCall]);

    // Render call button if not in call
    if (!isInCall) {
      return (
        <>
          <CallButton
            isHost={isHost}
            tourId={tourId}
            onClick={startCall}
            isStartingCall={isStartingCall}
            connectionState={connectionState}
          />

          <Snackbar
            open={showPermissionAlert}
            autoHideDuration={6000}
            onClose={() => setShowPermissionAlert(false)}
            anchorOrigin={{vertical: 'top', horizontal: 'center'}}
          >
            <Alert
              onClose={() => setShowPermissionAlert(false)}
              severity="warning"
              sx={{width: '100%'}}
            >
              Camera or microphone permissions are required for video chat.
            </Alert>
          </Snackbar>
        </>
      );
    }

    return (
      <>
        {/* Remote Video */}
        <CollapsibleVideo
          position="top"
          backgroundColor="#222"
          isCollapsed={remoteVideoCollapsed}
          setIsCollapsed={setRemoteVideoCollapsed}
        >
          <RemoteVideoContent
            connectionState={connectionState}
            remoteUsers={remoteUsers}
            isHost={isHost}
            reconnectAttempts={reconnectAttempts}
            connectionError={connectionError}
          />
        </CollapsibleVideo>

        {/* Local Video */}
        <CollapsibleVideo
          position="bottom"
          backgroundColor="#333"
          isCollapsed={localVideoCollapsed}
          setIsCollapsed={setLocalVideoCollapsed}
        >
          <LocalVideoContent
            isVideoActive={isVideoActive}
            mediaPermissions={mediaPermissions}
          />
        </CollapsibleVideo>

        {/* Connection error display */}
        <ConnectionErrorDisplay
          connectionError={connectionError || signalingError}
          connectionState={connectionState}
          reconnectAttempts={reconnectAttempts}
          resetConnection={handleResetConnection}
        />

        {/* Controls */}
        <CallControls
          isHost={isHost}
          isMicActive={isMicActive}
          isVideoActive={isVideoActive}
          isSyncActive={isSyncActive}
          connectionState={connectionState}
          connectionError={connectionError}
          reconnectAttempts={reconnectAttempts}
          toggleMic={toggleMic}
          toggleVideo={toggleVideo}
          toggleSync={toggleSync}
          leaveCall={leaveCall}
          resetConnection={handleResetConnection}
        />

        {/* Permission alert */}
        <Snackbar
          open={showPermissionAlert}
          autoHideDuration={6000}
          onClose={() => setShowPermissionAlert(false)}
          anchorOrigin={{vertical: 'top', horizontal: 'center'}}
        >
          <Alert
            onClose={() => setShowPermissionAlert(false)}
            severity="warning"
            sx={{width: '100%'}}
          >
            {!mediaPermissions.video && !mediaPermissions.audio
              ? "Camera and microphone access denied. Others won't be able to see or hear you."
              : !mediaPermissions.video
              ? "Camera access denied. Others won't be able to see you."
              : "Microphone access denied. Others won't be able to hear you."}
          </Alert>
        </Snackbar>
      </>
    );
  }
);
